package ru.it_mozaika.sender.js.application.frames.users

import ru.it_mozaika.sender.js.application.components.users.addUserDialog
import ru.it_mozaika.sender.js.application.context.BackdropContext
import ru.it_mozaika.sender.js.application.context.RequestResultContext
import io.ktor.client.call.*
import js.core.ReadonlyRecord
import js.core.jso
import mui.material.*
import mui.material.styles.Theme
import mui.material.styles.useTheme
import mui.system.responsive
import mui.system.sx
import react.*
import ru.it_mozaika.components.*
import ru.it_mozaika.sender.common.models.CreateUserRequest
import ru.it_mozaika.sender.common.models.ColumnsFilter
import ru.it_mozaika.sender.common.models.User
import ru.it_mozaika.sender.js.application.Http
import ru.it_mozaika.components.commonTableSkeletonFrame
import ru.it_mozaika.sender.js.application.components.users.deleteConfirmDialog
import ru.it_mozaika.sender.js.application.frames.MainCard
import tanstack.react.table.useReactTable
import tanstack.table.core.*
import web.cssom.*

val usersFrame = FC<PropsWithChildren> {
    val backDrop = useContext(BackdropContext)
    val requestResultProvider = useContext(RequestResultContext)
    val (users, setUsers) = useState<List<User>>(emptyList())
    /**
     * Значение из поля поиска
     * */
    val (searchQuery, setSearchQuery) = useState("")
    val (showFilter, setShowFilter) = useState(false)
    val (addUserDialogOpen, setAddUserDialogOpen) = useState(false)
    /**
     * Список отобранных свойств со значениями. Используется для фильтровки значений. Пока значений не много фильтрует "налету"
     * */
    val (propertiesFilter, setPropertiesFilter) = useState<List<ColumnsFilter>>(listOf())
    val (selectedProperty, setSelectedProperty) = useState<String>()
    val (checkedProperties, setCheckedProperties) = useState(listOf<String>())
    /**
     * Поскольку данных не много, пока, не стал писать запрос в базу. При необходимости словарь значений свойства по id можно получать из базы.
     */
    val propertiesDict = useMemo(users, selectedProperty) {
        when (selectedProperty) {
            "ID" -> ColumnsFilter(selectedProperty, users.map { it.id })
            "Email" -> ColumnsFilter(selectedProperty, users.map { it.email })
            "Уровень доступа" -> ColumnsFilter(selectedProperty, users.map { it.role.name })
            else -> null
        }
    }

    /**
     * Поскольку значений не много фильтруем юзеров по свойствам фильтров "налету"
     * Эти данные уже идут в таблицу
     * */
    val rowsData: List<User> = useMemo(users) {
        if (propertiesFilter.isNotEmpty()) {
            val userIdWithFilters = mutableListOf<String>() //собирает id юзеров у которых есть значения из фильтров
            propertiesFilter.forEach { property ->
                when (property.columnName) {
                    "ID" -> userIdWithFilters += property.values
                    "Email" -> userIdWithFilters += users.filter { it.email in property.values }.map { it.id }
                    "Имя" -> userIdWithFilters += users.filter { it.name in property.values }.map { it.id }
                    "Фамилия" -> userIdWithFilters += users.filter { it.lastName in property.values }.map { it.id }
                    "Уровень доступа" -> userIdWithFilters += users.filter { it.role.name in property.values }
                        .map { it.id }
                }
            }
            users.filter { it.id in userIdWithFilters }
        } else {
            users
        }
    }
    val columns = listOf("ID", "Email", "Имя", "Фамилия", "Уровень доступа") // Список колонок
    var (columnVisibility, setColumnVisibility) = useState<ReadonlyRecord<String, Boolean>>(jso {})
    val (columnOrder, setColumnOrder) = useState<ColumnOrderState>(jso {})
    val (showDeleteUserDialog, setShowDeleteUserDialog) = useState(false)

    /**
     * Список id пользователей
     * */
    val (selectedUsers, setSelectedUsers) = useState(listOf<User>())
    useEffect(selectedUsers) {
    }

    val table = useReactTable<User>(
        options = jso {
            this.columns = useMemo(columns) {
                columns.map { column ->
                    jso<ColumnDef<User, Any?>> {
                        accessorFn = { user, _ ->

                            when (column) {
                                "ID" -> user.id
                                "Email" -> user.email
                                "Имя" -> user.name
                                "Фамилия" -> user.lastName
                                "Уровень доступа" -> user.role
                                else -> {}
                            }

                        }
                        cell = ColumnDefTemplate { context ->
                            Box.create {
                                +context.getValue().toString()
                            }
                        }
                        id = column
                        header = StringOrTemplateHeader(column)
                    }
                }
            }.toTypedArray()
            this.data = useMemo(rowsData) { rowsData.toTypedArray() }
            this.state = jso {
                this.columnVisibility = columnVisibility
                this.columnOrder = columnOrder
            }
            this.onColumnVisibilityChange = {
                columnVisibility = it.unsafeCast<ReadonlyRecord<String, Boolean>>()
                setColumnVisibility(it.unsafeCast<ReadonlyRecord<String, Boolean>>())
            }
            this.onColumnOrderChange = {
                setColumnOrder(it.unsafeCast<Array<String>>())
            }
            this.getCoreRowModel = getCoreRowModel()
            this.debugTable = true
            this.debugHeaders = true
            this.debugColumns = true
        }
    )


    fun loadUsers() {
        backDrop.show()
        Http.get(
            path = "/accounts/list",
            query = mapOf("search" to searchQuery),
            callback = { response ->
                val result = response.body<List<User>>()
                setUsers(result)
                backDrop.hide()
            }
        )
        Http.get(
            path = "/accounts/count",
            query = mapOf("search" to searchQuery),
            callback = { response ->
                backDrop.hide()
            }
        )
    }

    fun createUser(request: CreateUserRequest) {
        backDrop.show()
        Http.post(
            "/accounts",
            body = request,
            callback = { response ->
                requestResultProvider.handleRequestRespond(response)
                backDrop.hide()
            }
        )
    }
    useEffectOnce {
        loadUsers()
    }
    useEffect(searchQuery) {
        loadUsers()
    }
    addUserDialog {
        this.handleOnClose = { setAddUserDialogOpen(false) }
        this.handleOnSubmit = { request ->
            createUser(request)
            loadUsers()
        }
        this.isOpen = addUserDialogOpen
    }
    deleteConfirmDialog {
        this.selected = selectedUsers
        isOpen = showDeleteUserDialog
        onClose = {
            setShowDeleteUserDialog(false)
            loadUsers()
        }
    }
    MainCard {
        title = "Пользователи"
        actions = Stack.create {
            this.direction = responsive(StackDirection.row)
            this.divider = Divider.create {
                orientation = Orientation.vertical
            }
            asDynamic().spacing = 2
            sx {
                this.width = 100.pct
                border = 2.px
                this.alignItems = AlignItems.center
                this.justifyContent = JustifyContent.spaceBetween
                this.alignContent = AlignContent.center
            }
            AvatarButton {
                onClick = {
                    setAddUserDialogOpen(true)

                }
                tooltipTitle = "Создать пользователя"
                mui.icons.material.AddCircleOutlineSharp()
            }

        }

        Toolbar {
            sx {
                display = Display.flex
                flexWrap = FlexWrap.wrap
                paddingTop = 7.px
            }
            Stack {
                this.direction = responsive(StackDirection.row)
                this.divider = Divider.create {
                    orientation = Orientation.vertical
                }
                asDynamic().spacing = 2
                sx {
                    this.width = 100.pct
                    border = 2.px
                    this.alignItems = AlignItems.center
                    this.justifyContent = JustifyContent.spaceBetween
                    this.alignContent = AlignContent.center
                }
                searchField {
                    this.onSearchQueryChange = {
                        setSearchQuery(it)
                    }
                }
            }
        }
        commonTableSkeletonFrame {
            currentSelected = selectedUsers.map { it.id }
            toolbarActions = Stack.create {
                this.direction = responsive(StackDirection.row)
                this.divider = Divider.create {
                    orientation = Orientation.vertical
                }
                asDynamic().spacing = 2
                sx {
                    this.width = 100.pct
                    border = 2.px
                    this.alignItems = AlignItems.center
                    this.justifyContent = JustifyContent.spaceBetween
                    this.alignContent = AlignContent.center
                }
                if (checkedProperties.isNotEmpty()) {
                    filterOffButton {
                        this.propertiesFilter = checkedProperties
                        this.handleOnFilterOffClick = {
                            setCheckedProperties(listOf())
                            setPropertiesFilter(listOf())
                            setSearchQuery("")
                        }
                    }
                }
                if (selectedUsers.isNotEmpty()) {
                    AvatarButton {
                        onClick = {
                            setShowDeleteUserDialog(true)
                        }
                        tooltipTitle = "Удалить пользователя"
                        palettecolor = useTheme<Theme>().palette.error
                        mui.icons.material.DeleteForeverSharp()
                    }
                    AvatarButton {
                        onClick = {
                            Http.post(
                                "/accounts/logout",
                                body = selectedUsers,
                            ) { response ->
                                requestResultProvider.handleRequestRespond(response)
                                loadUsers()
                            }
                        }
                        tooltipTitle = "Logout"
                        mui.icons.material.ExitToAppSharp()
                    }
                }
            }
            this.table = table
            this.dataCount = rowsData.size
            this.propertiesFilter = propertiesFilter
            this.onPropertySelect = { property ->
                setSelectedProperty(property)
                if (showFilter) setShowFilter(false) else setShowFilter(true)
            }
            this.columnId = "ID"
            this.label = "users"
            this.onSelectionChanged = { newSelected ->
                val newList = newSelected.mapNotNull { id ->
                    users.find { it.id == id }
                }
                setSelectedUsers(newList)
            }
            this.onDoubleClickHandler = {}
        }
    }
}


